import type { TrendPoint } from "@shared/types.js";
import { fnum, fstamp } from "../lib/format.js";
import { Kpi } from "../components/Charts.js";
import { useChartTip } from "../components/ChartTip.js";

function Delta({ now, prev, suffix = "" }: { now: number; prev?: number; suffix?: string }) {
  if (prev === undefined) return <span className="delta flat">&mdash;</span>;
  const d = now - prev;
  const cls = d > 0 ? "up" : d < 0 ? "down" : "flat";
  const sign = d > 0 ? "+" : "";
  return <span className={`delta ${cls}`}>{sign}{d.toFixed(suffix === "%" ? 1 : 0)}{suffix}</span>;
}

/** Multi-series line chart of completion and its components over snapshots. */
function TrendChart({ points }: { points: TrendPoint[] }) {
  const W = 760;
  const H = 260;
  const PAD = { t: 14, r: 16, b: 34, l: 44 };
  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;

  const series: { key: string; label: string; color: string; get: (p: TrendPoint) => number }[] = [
    { key: "complete", label: "% complete", color: "var(--s-done)", get: (p) => p.stats.completion },
    { key: "received", label: "% CR received", color: "var(--s-await)", get: (p) => (p.stats.total ? (p.stats.received / p.stats.total) * 100 : 0) },
    { key: "checked", label: "% crosschecked", color: "var(--s-est)", get: (p) => (p.stats.entered ? (p.stats.crosschecked / p.stats.entered) * 100 : 0) },
  ];

  const n = points.length;
  const x = (i: number) => PAD.l + (n <= 1 ? iw / 2 : (i / (n - 1)) * iw);
  const y = (v: number) => PAD.t + ih - (Math.max(0, Math.min(100, v)) / 100) * ih;
  const { bind, tipNode } = useChartTip();

  return (
    <div className="trendwrap">
      {tipNode}
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
        aria-label="Completion over time">
        {[0, 25, 50, 75, 100].map((g) => (
          <g key={g}>
            <line className="trend-grid" x1={PAD.l} x2={W - PAD.r} y1={y(g)} y2={y(g)} />
            <text className="trend-axis" x={PAD.l - 7} y={y(g) + 3} textAnchor="end">{g}%</text>
          </g>
        ))}

        {series.map((s) => {
          const d = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(s.get(p)).toFixed(1)}`).join("");
          return (
            <g key={s.key}>
              <path className="trend-line" d={d} stroke={s.color} />
              {points.map((p, i) => (
                <circle
                  key={p.snapshotId} className="trend-dot"
                  cx={x(i)} cy={y(s.get(p))} r={4} fill={s.color}
                  {...bind({
                    label: s.label,
                    value: `${s.get(p).toFixed(1)}%`,
                    note: fstamp(p.generatedAt),
                    color: s.color,
                  })}
                />
              ))}
            </g>
          );
        })}

        {points.map((p, i) => (
          <text
            key={p.snapshotId} className="trend-axis"
            x={x(i)} y={H - 12} textAnchor="middle"
          >
            {p.generatedAt.slice(0, 10)}
          </text>
        ))}
      </svg>

      <div className="rowflex" style={{ gap: 14, paddingTop: 8 }}>
        {series.map((s) => (
          <span className="rowflex" style={{ gap: 6, fontSize: 11.5, color: "var(--ink-2)" }} key={s.key}>
            <span className="sw" style={{ background: s.color, width: 9, height: 9, borderRadius: 2 }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export function Trend({
  points, includeCatE, onExport,
}: {
  points: TrendPoint[]; includeCatE: boolean; onExport: (kind: "csv" | "copy") => void;
}) {
  const last = points[points.length - 1];
  const prev = points[points.length - 2];

  if (points.length === 0) {
    return (
      <div className="card">
        <div className="empty">No snapshots yet. Upload a Masterfile to start tracking progress.</div>
      </div>
    );
  }

  return (
    <>
      <div className="sec">
        <div className="sec-head">
          <span className="eyebrow">Section 8</span>
          <h2><i className="bi bi-graph-up-arrow hico" aria-hidden="true" />Progress over time</h2>
          <span className="note">
            One point per uploaded Masterfile &middot; {points.length} snapshot
            {points.length === 1 ? "" : "s"}
            {includeCatE ? " · Cat. E included" : " · Cat. E excluded"}
          </span>
        </div>

        <div className="grid g-kpi">
          <Kpi icon="bi-graph-up-arrow" label="Latest completion" value={`${last!.stats.completion.toFixed(1)}%`} tone="done"
            foot={<>vs previous <Delta now={last!.stats.completion} prev={prev?.stats.completion} suffix="%" /></>} />
          <Kpi icon="bi-pencil-square" label="Entered" value={fnum(last!.stats.entered)} tone="done"
            foot={<>vs previous <Delta now={last!.stats.entered} prev={prev?.stats.entered} /></>} />
          <Kpi icon="bi-envelope-open" label="CR received" value={fnum(last!.stats.received)} tone="await"
            foot={<>vs previous <Delta now={last!.stats.received} prev={prev?.stats.received} /></>} />
          <Kpi icon="bi-hourglass-split" label="Awaiting pricing" value={fnum(last!.stats.awaiting)} tone="await"
            foot={<>vs previous <Delta now={last!.stats.awaiting} prev={prev?.stats.awaiting} /></>} />
          <Kpi icon="bi-shield-check" label="Crosschecked" value={fnum(last!.stats.crosschecked)}
            foot={<>vs previous <Delta now={last!.stats.crosschecked} prev={prev?.stats.crosschecked} /></>} />
        </div>
      </div>

      <div className="sec">
        <div className="card">
          <div className="card-head">
            <h3><i className="bi bi-activity hico" aria-hidden="true" />Completion curve</h3>
            <span className="sub">Each line is a share of its own base, so they are comparable</span>
          </div>
          {points.length === 1 ? (
            <div className="empty">
              Only one snapshot so far &mdash; upload a second Masterfile to see movement.
            </div>
          ) : (
            <TrendChart points={points} />
          )}
        </div>
      </div>

      <div className="sec">
        <div className="card">
          <div className="card-head">
            <h3><i className="bi bi-clock-history hico" aria-hidden="true" />Snapshot by snapshot</h3>
            <div style={{ display: "flex", gap: 7, marginLeft: "auto" }}>
              <button className="btn btn--sm btn--primary" type="button" onClick={() => onExport("csv")}>
                Download CSV
              </button>
              <button className="btn btn--sm" type="button" onClick={() => onExport("copy")}>
                Copy for Excel
              </button>
            </div>
          </div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Snapshot</th><th>Parsed</th>
                  <th className="n">Records</th><th className="n">Received</th>
                  <th className="n">Priced</th><th className="n">Estimated</th>
                  <th className="n">Entered</th><th className="n">Crosschecked</th>
                  <th className="n">% complete</th><th className="n">Change</th>
                </tr>
              </thead>
              <tbody>
                {points.map((p, i) => {
                  const before = points[i - 1];
                  return (
                    <tr key={p.snapshotId}>
                      <td className="strong">{p.label ?? p.sourceFile}</td>
                      <td>{fstamp(p.generatedAt)}</td>
                      <td className="n">{fnum(p.stats.total)}</td>
                      <td className="n">{fnum(p.stats.received)}</td>
                      <td className="n">{fnum(p.stats.priced)}</td>
                      <td className="n">{fnum(p.stats.estimated)}</td>
                      <td className="n">{fnum(p.stats.entered)}</td>
                      <td className="n">{fnum(p.stats.crosschecked)}</td>
                      <td className="n strong">{p.stats.completion.toFixed(1)}%</td>
                      <td className="n">
                        <Delta now={p.stats.completion} prev={before?.stats.completion} suffix="%" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="tblnote">
            Snapshots are immutable: each upload is stored whole, so these figures never change
            retroactively. The Cat. E toggle in the filter bar applies to this table too.
          </div>
        </div>
      </div>
    </>
  );
}
